import {
  collection, limit, onSnapshot, orderBy, query, Timestamp, where,
} from 'firebase/firestore';
import { useEffect } from 'react';

import { db } from '@/config';
import { InAppNotification } from '@/types/inAppNotification.types';
import {
  AlertSound, isAlertSoundUnlocked, playAlertSound, unlockAlertSound,
} from '@/utils/alertSound';
import { getTranslatedContent } from '@/utils/inAppNotification.utils';
import { isAlertSoundEnabled, isSystemNotificationEnabled } from '@/utils/notificationPreferences';
import { showLocalNotification } from '@/utils/webPush';

/**
 * Alerty (dźwięk + powiadomienie systemowe) dla powiadomień z bazy.
 *
 * JEDNO ŹRÓDŁO PRAWDY: `users/{uid}/notifications`. O tym, CZY coś jest powiadomieniem,
 * decyduje wyłącznie backend (guardy `internalOrder`, `unpaid`, docelowo role) — panel
 * ma to tylko zagrać i pokazać. Wcześniej nasłuchiwaliśmy równolegle surowych kolekcji
 * (`orders`, `tasks`, …) i reguły trzeba było pisać dwa razy; efektem był dzwonek przy
 * zamówieniu `unpaid`, którego nigdzie nie było widać.
 */

/** Dwa zdarzenia w tej samej sekundzie to jeden dzwonek — inaczej alert się zlewa w hałas. */
const SOUND_DEBOUNCE_MS = 3000;
/** Ile ostatnich powiadomień obserwujemy. Alert dotyczy świeżych, nie historii. */
const WATCH_LIMIT = 20;
/** Klucz roszczenia w localStorage — chroni przed dzwonkiem z każdej otwartej karty naraz. */
const CLAIM_PREFIX = 'hs.notifAlert.';
const CLAIM_TTL_MS = 60_000;
/** Zapas na rozjazd zegara klienta i serwera przy rozpoznawaniu zaległości po wybudzeniu. */
const BACKLOG_MARGIN_MS = 10_000;

let lastSoundAt = 0;

/**
 * Gest odblokowujący dźwięk to `pointerup`/`touchend`/`click` — `pointerdown` z palca nie
 * jest aktywacją użytkownika, więc na telefonie odblokowanie było odrzucane. Nasłuch
 * podpinamy dopiero w zalogowanym panelu: wcześniej wisiał od importu modułu i pierwsze
 * dotknięcie ekranu logowania grało dzwonek.
 */
const UNLOCK_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'] as const;

const listenForUnlock = () => {
  if (isAlertSoundUnlocked()) return () => undefined;
  UNLOCK_EVENTS.forEach((event) => window.addEventListener(event, unlockAlertSound, { passive: true }));
  return () => UNLOCK_EVENTS.forEach((event) => window.removeEventListener(event, unlockAlertSound));
};

const ringAlert = (sound: AlertSound) => {
  if (!isAlertSoundEnabled()) return;

  const now = Date.now();
  if (now - lastSoundAt < SOUND_DEBOUNCE_MS) return;
  lastSoundAt = now;

  // Dźwięk gra też przy karcie w tle: recepcja przełącza się na PMS i ma usłyszeć zamówienie.
  playAlertSound(sound).catch((error) => {
    // eslint-disable-next-line no-console
    console.warn('[notifications] nie udało się odtworzyć dźwięku', error);
  });
};

/**
 * Moment, od którego karta znów jest widoczna.
 *
 * Telefon zamraża JavaScript karty w tle (iOS od razu po zablokowaniu ekranu). Push
 * z backendu dzwoni wtedy dźwiękiem systemowym, a po wybudzeniu Firestore dosyła
 * zaległe powiadomienia hurtem — panel grał drugi, spóźniony dzwonek. Powiadomienie
 * utworzone PRZED powrotem karty, a doręczone dopiero PO nim, to zaległość: bez dźwięku.
 * Na desktopie karta w tle działa, więc takie powiadomienie przychodzi jeszcze w tle
 * i gra normalnie.
 */
let visibleSince = Date.now();
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) visibleSince = Date.now();
  });
}

const isBacklog = (createdAtMs: number) => (
  !document.hidden && createdAtMs < visibleSince - BACKLOG_MARGIN_MS
);

/**
 * Roszczenie do alertu, wspólne dla kart tej samej przeglądarki.
 *
 * Bez tego pracownik z panelem otwartym w trzech kartach słyszy trzy dzwonki naraz —
 * wcześniej maskował to warunek „tylko aktywna karta", którego już nie ma.
 */
const claimAlert = (notificationId: string): boolean => {
  const key = `${CLAIM_PREFIX}${notificationId}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing && Date.now() - Number(existing) < CLAIM_TTL_MS) return false;
    localStorage.setItem(key, String(Date.now()));

    // Sprzątanie po sobie — klucze alertów nie mogą rosnąć w nieskończoność.
    Object.keys(localStorage)
      .filter((entry) => entry.startsWith(CLAIM_PREFIX))
      .forEach((entry) => {
        const stamp = Number(localStorage.getItem(entry));
        if (!stamp || Date.now() - stamp > CLAIM_TTL_MS) localStorage.removeItem(entry);
      });

    return true;
  } catch {
    // Brak localStorage (tryb prywatny) — lepiej zadzwonić dwa razy niż wcale.
    return true;
  }
};

/**
 * `tag` dymka systemowego — MUSI być ten sam, którym oznacza push backend
 * (`createNotificationPayload`: `orderId` → `reservationId` → `taskId`).
 *
 * Na tym tagu opiera się całe odróżnianie „to samo zdarzenie" po stronie przeglądarki:
 * zgodne tagi = dymek z pusha PODMIENIA ten z karty. Tag brany z id dokumentu
 * powiadomienia nigdy nie pasował do pusha, więc o jednej zmianie statusu recepcja
 * dostawała dwa dymki. Gdy zdarzenie nie ma żadnego z tych identyfikatorów, zostaje
 * id dokumentu — lepiej dwa dymki niż zlepienie dwóch różnych zdarzeń w jeden.
 */
const notificationTag = (notification: InAppNotification): string => {
  const metadata = notification.metadata || {};
  const source = metadata.orderId || metadata.reservationId || metadata.taskId;
  return typeof source === 'string' && source ? source : notification.id;
};

/** Powiadomienie systemowe pokazujemy tylko, gdy karty nie widać — inaczej dubluje UI panelu. */
const showSystemNotification = (notification: InAppNotification, title: string, body: string) => {
  if (!isSystemNotificationEnabled()) return;
  // Dymek tylko przy karcie w tle — przy otwartym panelu dubluje to, co widać w dzwonku.
  if (!document.hidden) return;

  showLocalNotification(title, { body, tag: notificationTag(notification), icon: '/logo192.png' })
    .catch(() => undefined);
};

const soundFor = (notification: InAppNotification): AlertSound => {
  const event = String(notification.metadata?.event || '');
  return event.endsWith('.cancelled') ? 'cancelled' : 'new';
};

export const useNotificationAlerts = (
  userId: string | undefined,
  hotelId: string | null | undefined,
  language = 'pl',
) => {
  useEffect(() => {
    if (!userId || !hotelId) return undefined;

    const stopUnlockListener = listenForUnlock();

    // Alert dotyczy tego, co przyszło PO wejściu do panelu. Bez tego odcięcia każde
    // wejście odtwarzałoby historię, a po zmianie hotelu — powiadomienia z drugiego.
    const mountedAt = Timestamp.now();
    const alerted = new Set<string>();

    const notificationsQuery = query(
      collection(db, 'users', userId, 'notifications'),
      where('hotelId', '==', hotelId),
      orderBy('createdAt', 'desc'),
      limit(WATCH_LIMIT),
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        if (snapshot.metadata.hasPendingWrites) return;

        snapshot.docChanges().forEach((change) => {
          /* `added` zamiast porównywania rozmiaru snapshotu: licznik gubił zdarzenie,
             gdy w tej samej chwili ktoś oznaczył inne powiadomienie jako przeczytane. */
          if (change.type !== 'added') return;

          const notification = { id: change.doc.id, ...change.doc.data() } as InAppNotification;
          const createdAt = notification.createdAt as Timestamp | undefined;
          if (!createdAt?.toMillis || createdAt.toMillis() <= mountedAt.toMillis()) return;
          if (alerted.has(notification.id)) return;
          alerted.add(notification.id);
          if (isBacklog(createdAt.toMillis())) return;

          if (!claimAlert(notification.id)) return;

          ringAlert(soundFor(notification));
          showSystemNotification(
            notification,
            getTranslatedContent(notification.title, language),
            getTranslatedContent(notification.description, language),
          );
        });
      },
      (error) => {
        /* Cichy błąd listenera = brak dzwonka BEZ żadnego objawu (np. gdy zabraknie
           indeksu złożonego `hotelId + createdAt`). Wypisujemy go, żeby dało się to
           odróżnić od „po prostu nic nie przyszło". */
        // eslint-disable-next-line no-console
        console.error('[notifications] listener powiadomień padł', error);
      },
    );

    return () => {
      unsubscribe();
      stopUnlockListener();
    };
  }, [userId, hotelId, language]);
};
